import { useState } from 'react';
import { Button, Chip, IconButton, Stack, TextField, Tooltip, Snackbar, Alert } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ExportMenu } from '../../components/ExportMenu';
import { DataTable, type Column } from '../../components/DataTable';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { TechnicianFormDialog } from './TechnicianFormDialog';
import { techniciansApi, type Technician, type CreateTechnicianInput } from '../../api/technicians.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

export function TechnicianListPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Technician | null>(null);
  const [deleting, setDeleting] = useState<Technician | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['technicians', page, limit, search],
    queryFn: () => techniciansApi.list({ page, limit, search: search || undefined }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['technicians'] });

  const createMut = useMutation({
    mutationFn: (input: CreateTechnicianInput) => techniciansApi.create(input),
    onSuccess: () => { setFormOpen(false); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const updateMut = useMutation({
    mutationFn: ({ id, input }: { id: number; input: CreateTechnicianInput }) => techniciansApi.update(id, input),
    onSuccess: () => { setFormOpen(false); setEditing(null); invalidate(); },
    onError: (e) => setFormError(extractApiError(e)),
  });
  const deleteMut = useMutation({
    mutationFn: (id: number) => techniciansApi.remove(id),
    onSuccess: () => { setDeleting(null); invalidate(); },
    onError: (e) => { setToast(extractApiError(e)); setDeleting(null); },
  });

  const columns: Column<Technician>[] = [
    { key: 'technicianCode', label: 'Code', width: 110 },
    { key: 'technicianName', label: 'Name' },
    { key: 'skillCategory', label: 'Skill' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={r.status === 'active' ? 'success' : 'default'} /> },
  ];

  return (
    <>
      <PageHeader
        title="Technician Master"
        action={
          <Stack direction="row" spacing={1}>
            <ExportMenu dataset="technicians" permission="technician.export" filters={{ search }} />
            {hasPermission('technician.create') && <Button startIcon={<Add />} variant="contained" onClick={() => { setEditing(null); setFormError(null); setFormOpen(true); }}>New Technician</Button>}
          </Stack>
        }
      />
      <Stack direction="row" mb={2}>
        <TextField size="small" placeholder="Search name / code / skill" value={search} onChange={(e) => { setSearch(e.target.value); setPage(1); }} />
      </Stack>
      <DataTable
        columns={columns}
        rows={data?.data ?? []}
        loading={isLoading}
        page={page}
        limit={limit}
        total={data?.meta.total ?? 0}
        onPageChange={setPage}
        onLimitChange={(l) => { setLimit(l); setPage(1); }}
        rowKey={(r) => r.id}
        actions={(r) => (
          <>
            {hasPermission('technician.update') && <Tooltip title="Edit"><IconButton size="small" onClick={() => { setEditing(r); setFormError(null); setFormOpen(true); }}><Edit fontSize="small" /></IconButton></Tooltip>}
            {hasPermission('technician.delete') && <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleting(r)}><Delete fontSize="small" /></IconButton></Tooltip>}
          </>
        )}
      />
      <TechnicianFormDialog
        open={formOpen}
        initial={editing}
        error={formError}
        onClose={() => setFormOpen(false)}
        onSubmit={async (input) => {
          setFormError(null);
          if (editing) await updateMut.mutateAsync({ id: editing.id, input });
          else await createMut.mutateAsync(input);
        }}
      />
      <ConfirmDialog
        open={Boolean(deleting)}
        title="Delete technician"
        message={`Soft-delete ${deleting?.technicianName}?`}
        confirmLabel="Delete"
        onCancel={() => setDeleting(null)}
        onConfirm={() => deleting && deleteMut.mutate(deleting.id)}
      />
      <Snackbar open={Boolean(toast)} autoHideDuration={6000} onClose={() => setToast(null)}>
        <Alert severity="warning" onClose={() => setToast(null)}>{toast}</Alert>
      </Snackbar>
    </>
  );
}
